import {AChartX} from "@atiautomacao/ati-ui-library";
import React, {FunctionComponent} from "react";
import {useTheme} from "@mui/material";
import {useAppSelector} from "../../../../Config/Hooks";

export interface DonutItem {
    name: string;
    value: number;
    color: string;
}

interface Props {
    items: DonutItem[];
    height?: number;
}

const EMPTY_SLICE_COLOR = "#e0e0e0";

/**
 * Reusable donut (pie) chart for the command audit screen.
 * - Central value totals the slices.
 * - Values are shown beside each slice.
 * - When there is no data (total === 0), renders a single grey slice with the central "0",
 *   as required by the ticket.
 */
const CommandDonutChart: FunctionComponent<Props> = ({items, height = 300}) => {
    const openSubMenu = useAppSelector((state: any) => state.layout.openSubMenu);
    const theme = useTheme();

    const total = React.useMemo(
        () => items.reduce((sum, item) => sum + (item.value ?? 0), 0),
        [items]
    );
    const hasData = total > 0;

    // @ts-ignore
    const option: AChartXProps['option'] = React.useMemo(() => {
        const seriesData = hasData
            ? items.map(item => ({
                name: item.name,
                value: item.value,
                itemStyle: {color: item.color},
                // fatia com valor 0 não exibe rótulo/linha (show explícito -> merge-safe)
                label: {show: item.value !== 0},
                labelLine: {show: item.value !== 0},
            }))
            : [{
                name: "Sem dados",
                value: 1,
                itemStyle: {color: EMPTY_SLICE_COLOR},
                label: {show: false},
                labelLine: {show: false},
            }];

        return {
            color: items.map(item => item.color),
            toolbox: {show: false},
            tooltip: {
                show: hasData,
                trigger: 'item',
                formatter: (params: any) => `${params.name}<br/>Comandos: <b>${params.value}</b> (${params.percent}%)`
            },
            legend: {
                show: hasData,
                type: 'scroll',
                orient: 'horizontal',
                bottom: 0,
                left: 'center',
                icon: 'circle',
                itemWidth: 10,
                itemHeight: 10,
                data: items.map(item => item.name),
            },
            graphic: {
                type: 'text',
                left: 'center',
                top: '40%',
                z: 10,
                style: {
                    text: String(total),
                    textAlign: 'center',
                    textVerticalAlign: 'middle',
                    fontSize: 32,
                    fontWeight: 'normal',
                    fontFamily: 'Work Sans',
                    fill: theme.palette.mode === 'light' ? '#000000' : '#ffffff',
                }
            },
            series: [{
                type: 'pie',
                radius: ['48%', '70%'],
                center: ['50%', '45%'],
                avoidLabelOverlap: true,
                label: {
                    show: hasData,
                    position: 'outside',
                    formatter: '{c}',
                    fontSize: 20,
                    fontWeight: 'normal',
                    fontFamily: 'Work Sans',
                },
                labelLine: {show: hasData, length: 10, length2: 10},
                emphasis: {
                    scale: hasData,
                    label: {show: hasData, fontWeight: 'bold'}
                },
                data: seriesData,
            }]
        };
    }, [items, total, hasData, theme.palette.mode]);

    return (
        <AChartX
            key={`command-donut-${openSubMenu}`}
            option={option}
            width={'100%'}
            height={height}
            loading={false}
            theme={theme.palette.mode}
        />
    );
};

export default CommandDonutChart;
